import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { UserPlus, Pencil, Trash2, Loader2, Camera, X, Wallet, Check, Ban, BadgeCheck, Copy, Building2, Users, Power, RotateCcw } from "lucide-react";
import BrandRulesModal from "./MandoobBrandRules";
import MandoobCustomersModal from "./MandoobCustomersModal";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (n) => Number(n || 0).toLocaleString("en-US", { maximumFractionDigits: 0 });
const PHOTOS = [
  ["personal_photo", "الصورة الشخصية"], ["id_front", "الهوية (أمام)"], ["id_back", "الهوية (خلف)"],
  ["residence_card", "بطاقة السكن"], ["extra_photo", "صورة إضافية"],
];
const EMPTY = { full_name: "", phone: "", password: "", job_title: "مندوب مبيعات", salary: "", commission_pct: "",
  monthly_target: "", national_id: "", address: "", notes: "" };

const PhotoSlot = ({ k, label, value, onChange }) => {
  const [busy, setBusy] = useState(false);
  const pick = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    const fd = new FormData(); fd.append("file", f);
    try { const { data } = await axios.post(`${API}/admin/upload-image`, fd); onChange(k, data.path); }
    catch { toast.error("فشل رفع الصورة"); } finally { setBusy(false); }
  };
  return (
    <label className="relative rounded-2xl border-2 border-dashed border-blue-200 bg-blue-50/50 aspect-square flex flex-col items-center justify-center cursor-pointer overflow-hidden" data-testid={`mandoob-photo-${k}`}>
      {value ? <img src={`${API}/files/${value}`} alt="" className="absolute inset-0 w-full h-full object-cover" /> :
        busy ? <Loader2 className="w-6 h-6 animate-spin text-blue-600" /> : <Camera className="w-6 h-6 text-blue-500" />}
      <span className="relative z-10 mt-auto w-full text-center text-[10px] font-bold bg-white/90 py-1">{label}</span>
      <input type="file" accept="image/*" className="hidden" onChange={pick} />
    </label>
  );
};

const Field = ({ label, k, form, set, type = "text", dir }) => (
  <label className="block">
    <span className="text-[11px] font-bold text-slate-600">{label}</span>
    <input type={type} dir={dir} value={form[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`mandoob-field-${k}`}
      className="mt-1 w-full border-2 border-slate-200 rounded-xl px-3 py-2 text-sm focus:border-blue-600 outline-none" />
  </label>
);

const MandoobForm = ({ initial, onClose, onSaved }) => {
  const [form, setForm] = useState(initial ? { ...EMPTY, ...initial, password: "" } : EMPTY);
  const [busy, setBusy] = useState(false);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const save = async () => {
    setBusy(true);
    try {
      if (initial) await axios.patch(`${API}/admin/mandoobs/${initial.id}`, form);
      else await axios.post(`${API}/admin/mandoobs`, form);
      toast.success("تم الحفظ"); onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "فشل الحفظ"); } finally { setBusy(false); }
  };
  return (
    <div className="fixed inset-0 z-50 bg-black/60 flex items-end sm:items-center justify-center" dir="rtl" data-testid="mandoob-form">
      <div className="bg-white w-full sm:max-w-2xl max-h-[92vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl p-5 space-y-4">
        <div className="flex items-center justify-between">
          <h3 className="font-black text-lg text-[#0B1E4A]">{initial ? "تعديل المندوب" : "إضافة مندوب"}</h3>
          <button onClick={onClose} data-testid="mandoob-form-close" className="w-9 h-9 rounded-full bg-slate-100 flex items-center justify-center"><X className="w-5 h-5" /></button>
        </div>
        <div className="grid grid-cols-2 gap-3">
          <Field label="الاسم الكامل *" k="full_name" form={form} set={set} />
          <Field label="الوظيفة" k="job_title" form={form} set={set} />
          <Field label="رقم الدخول * (07xxxxxxxxx)" k="phone" form={form} set={set} dir="ltr" />
          <Field label={initial ? "رمز جديد (اتركه فارغ)" : "الرمز *"} k="password" form={form} set={set} dir="ltr" />
          <Field label="الراتب (د.ع)" k="salary" form={form} set={set} type="number" dir="ltr" />
          <Field label="رقم الهوية" k="national_id" form={form} set={set} dir="ltr" />
          <Field label="نسبة الأرباح %" k="commission_pct" form={form} set={set} type="number" dir="ltr" />
          <Field label="التارجت الشهري (د.ع)" k="monthly_target" form={form} set={set} type="number" dir="ltr" />
        </div>
        <Field label="العنوان / السكن" k="address" form={form} set={set} />
        <Field label="ملاحظات" k="notes" form={form} set={set} />
        <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
          {PHOTOS.map(([k, l]) => <PhotoSlot key={k} k={k} label={l} value={form[k]} onChange={set} />)}
        </div>
        <button onClick={save} disabled={busy} data-testid="mandoob-form-save"
          className="w-full bg-[#1E3A8A] hover:bg-[#0B1E4A] text-white font-black rounded-2xl py-3 flex items-center justify-center gap-2 disabled:opacity-50">
          {busy && <Loader2 className="w-4 h-4 animate-spin" />} حفظ
        </button>
      </div>
    </div>
  );
};

const MandoobCard = ({ m, onEdit, onDelete, onRules, onCustomers, onToggle, onReset }) => {
  const on = m.active !== false;
  const s = m.stats || {};
  const copy = () => {
    navigator.clipboard?.writeText(`رابط الدخول: ${window.location.origin}/login\nالرقم: ${m.phone}`);
    toast.success("تم نسخ معلومات الدخول");
  };
  return (
    <div className={`bg-white rounded-3xl border-2 p-4 space-y-3 ${on ? "border-blue-100" : "border-red-300 bg-red-50/40"}`} data-testid={`mandoob-card-${m.id}`}>
      <div className="flex gap-2">
        <button onClick={() => onToggle(m)} data-testid={`mandoob-toggle-${m.id}`}
          className={`flex-1 rounded-xl py-2 text-xs font-black flex items-center justify-center gap-1.5 text-white ${on ? "bg-emerald-600" : "bg-red-600"}`}>
          <Power className="w-4 h-4" /> {on ? "شغّال — اضغط للإيقاف" : "موقوف (ممنوع البيع) — اضغط للتشغيل"}
        </button>
        <button onClick={() => onReset(m)} data-testid={`mandoob-reset-${m.id}`}
          className="rounded-xl px-3 py-2 text-xs font-black flex items-center gap-1 text-white bg-violet-600">
          <RotateCcw className="w-4 h-4" /> تصفير
        </button>
      </div>
      <div className="flex items-center gap-3">
        {m.personal_photo ? <img src={`${API}/files/${m.personal_photo}`} alt="" className="w-14 h-14 rounded-2xl object-cover" /> :
          <div className="w-14 h-14 rounded-2xl bg-blue-100 flex items-center justify-center font-black text-blue-700 text-xl">{(m.full_name || "?")[0]}</div>}
        <div className="flex-1 min-w-0">
          <p className="font-black text-[#0B1E4A] truncate">{m.full_name}</p>
          <p className="text-[11px] text-slate-500">{m.job_title} · <span dir="ltr">{m.phone}</span></p>
          <p className="text-[11px] text-slate-500">الراتب <span dir="ltr">{fmt(m.salary)}</span> · {m.customers_count} زبون</p>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-2 text-center">
        <div className="bg-blue-50 rounded-xl p-2"><p className="text-[10px] text-slate-500">مبيعات الدورة</p><p className="font-black text-sm" dir="ltr">{fmt(s.month_sales)}</p></div>
        <div className="bg-amber-50 rounded-xl p-2"><p className="text-[10px] text-slate-500">التارجت</p><p className="font-black text-sm" dir="ltr">{fmt(s.monthly_target)}</p></div>
        <div className="bg-emerald-50 rounded-xl p-2"><p className="text-[10px] text-slate-500">الأرباح ({s.commission_pct || 0}%)</p><p className="font-black text-sm text-emerald-700" dir="ltr">{fmt(s.earned)}</p></div>
      </div>
      <button onClick={() => onRules(m)} data-testid={`mandoob-rules-${m.id}`}
        className="w-full text-white text-xs font-black rounded-xl py-2.5 flex items-center justify-center gap-1.5" style={{ background: "linear-gradient(135deg,#F59E0B,#B45309)" }}>
        <Building2 className="w-4 h-4" /> نسب وتارجت الشركات
      </button>
      <button onClick={() => onCustomers(m)} data-testid={`mandoob-customers-${m.id}`}
        className="w-full text-white text-xs font-black rounded-xl py-2.5 flex items-center justify-center gap-1.5" style={{ background: "linear-gradient(135deg,#0EA5E9,#0369A1)" }}>
        <Users className="w-4 h-4" /> الزبائن ({m.customers_count})
      </button>
      <div className="flex gap-2">
        <button onClick={() => onEdit(m)} data-testid={`mandoob-edit-${m.id}`} className="flex-1 bg-[#1E3A8A] text-white text-xs font-bold rounded-xl py-2 flex items-center justify-center gap-1"><Pencil className="w-3.5 h-3.5" /> تعديل</button>
        <button onClick={copy} data-testid={`mandoob-copy-${m.id}`} className="flex-1 bg-slate-100 text-slate-700 text-xs font-bold rounded-xl py-2 flex items-center justify-center gap-1"><Copy className="w-3.5 h-3.5" /> معلومات الدخول</button>
        <button onClick={() => onDelete(m)} data-testid={`mandoob-delete-${m.id}`} className="w-10 bg-red-50 text-red-600 rounded-xl flex items-center justify-center"><Trash2 className="w-4 h-4" /></button>
      </div>
    </div>
  );
};

const Withdrawals = ({ rows, onDecide }) => rows.length === 0 ? <p className="text-center text-xs text-slate-400 py-3">ما اكو طلبات سحب</p> : (
  <div className="bg-white rounded-3xl border-2 border-emerald-100 p-4 space-y-2" data-testid="mandoob-withdrawals-admin">
    <p className="font-black text-emerald-800 flex items-center gap-2"><Wallet className="w-4 h-4" /> طلبات سحب الأرباح</p>
    {rows.map((w) => (
      <div key={w.id} className="flex items-center gap-2 text-sm border-t border-slate-100 pt-2">
        <span className="flex-1 font-bold">{w.mandoob_name}</span>
        <span className="font-black" dir="ltr">{fmt(w.amount)}</span>
        {w.status === "pending" ? (
          <>
            <button onClick={() => onDecide(w.id, "approve")} data-testid={`withdraw-approve-${w.id}`} className="w-8 h-8 rounded-full bg-emerald-600 text-white flex items-center justify-center"><Check className="w-4 h-4" /></button>
            <button onClick={() => onDecide(w.id, "reject")} data-testid={`withdraw-reject-${w.id}`} className="w-8 h-8 rounded-full bg-red-600 text-white flex items-center justify-center"><Ban className="w-4 h-4" /></button>
          </>
        ) : <span className="text-[11px] font-bold">{w.status === "approved" ? "✅ مصروف" : "❌ مرفوض"}</span>}
      </div>
    ))}
  </div>
);

const MandoobsTab = () => {
  const [list, setList] = useState([]);
  const [wd, setWd] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(undefined);
  const [rulesFor, setRulesFor] = useState(null);
  const [custFor, setCustFor] = useState(null);
  const [showWd, setShowWd] = useState(false);
  const pendingWd = wd.filter((w) => w.status === "pending").length;
  const toggle = async (m) => {
    const next = m.active === false;
    if (!next && !window.confirm(`إيقاف ${m.full_name}؟ ما يگدر يبيع لحد ما تشغّله.`)) return;
    try { await axios.patch(`${API}/admin/mandoobs/${m.id}`, { active: next }); toast.success(next ? "تم التشغيل" : "تم الإيقاف"); load(); }
    catch { toast.error("فشل"); }
  };
  const reset = async (m) => {
    const s = m.stats || {};
    if (!window.confirm(`تصفير ${m.full_name}؟\nأرباحه الحالية: ${fmt(s.earned)} د.ع\nمبيعات الدورة: ${fmt(s.month_sales)} د.ع\nالأرباح والتارجت يرجعون صفر ويبدي حساب جديد.`)) return;
    try { await axios.post(`${API}/admin/mandoobs/${m.id}/reset-cycle`); toast.success("تم التصفير"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "فشل"); }
  };
  const load = async () => {
    setLoading(true);
    try {
      const [a, b] = await Promise.all([axios.get(`${API}/admin/mandoobs`), axios.get(`${API}/admin/mandoob-withdrawals`)]);
      setList(a.data || []); setWd(b.data || []);
    } catch { toast.error("فشل التحميل"); } finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);
  const del = async (m) => {
    if (!window.confirm(`حذف المندوب ${m.full_name}؟`)) return;
    try { await axios.delete(`${API}/admin/mandoobs/${m.id}`); toast.success("تم الحذف"); load(); } catch { toast.error("فشل الحذف"); }
  };
  const decide = async (id, action) => {
    if (action === "approve" && !window.confirm("موافقة وصرف الأرباح؟ أرباحه والتارجت يتصفّرون ويبدي من جديد.")) return;
    try { await axios.post(`${API}/admin/mandoob-withdrawals/${id}/${action}`); load(); } catch (e) { toast.error(e.response?.data?.detail || "فشل"); }
  };
  return (
    <div className="space-y-4" dir="rtl" data-testid="mandoobs-tab">
      <div className="rounded-3xl p-5 text-white flex items-center gap-3 shadow-lg" style={{ background: "linear-gradient(135deg,#1E3A8A,#0B1E4A)" }}>
        <BadgeCheck className="w-8 h-8" />
        <div className="flex-1"><p className="font-black text-lg">المندوبين</p><p className="text-[11px] opacity-80">{list.length} مندوب</p></div>
        <button onClick={() => setShowWd((v) => !v)} data-testid="mandoob-withdrawals-btn"
          className="relative bg-emerald-500 text-white font-black text-sm rounded-full px-4 py-2 flex items-center gap-1">
          <Wallet className="w-4 h-4" /> طلبات سحب الأرباح
          {pendingWd > 0 && <span data-testid="mandoob-withdrawals-badge" className="absolute -top-2 -left-2 min-w-[22px] h-[22px] rounded-full bg-red-600 text-white text-[11px] flex items-center justify-center ring-2 ring-white">{pendingWd}</span>}
        </button>
        <button onClick={() => setEditing(null)} data-testid="mandoob-add-btn" className="bg-white text-[#1E3A8A] font-black text-sm rounded-full px-4 py-2 flex items-center gap-1"><UserPlus className="w-4 h-4" /> إضافة</button>
      </div>
      {(showWd || pendingWd > 0) && <Withdrawals rows={wd} onDecide={decide} />}
      {loading ? <div className="py-10 flex justify-center"><Loader2 className="w-6 h-6 animate-spin text-blue-700" /></div> : (
        <div className="grid sm:grid-cols-2 gap-3">{list.map((m) => <MandoobCard key={m.id} m={m} onEdit={setEditing} onDelete={del} onRules={setRulesFor} onCustomers={setCustFor} onToggle={toggle} onReset={reset} />)}</div>
      )}
      {custFor && <MandoobCustomersModal mandoob={custFor} onClose={() => setCustFor(null)} />}
      {rulesFor && <BrandRulesModal mandoob={rulesFor} onClose={() => { setRulesFor(null); load(); }} />}
      {editing !== undefined && <MandoobForm initial={editing} onClose={() => setEditing(undefined)} onSaved={() => { setEditing(undefined); load(); }} />}
    </div>
  );
};

export default MandoobsTab;
